/**
 * Canal d'acquisition « en clair » : UTM de l'URL + domaine du site référent.
 *
 * ⚠️ Ces signaux sont volontairement HORS du régime de consentement marketing, à la
 * différence des identifiants de clic publicitaire (`fbclid`, `oppref`, `_fbp`…) :
 *   - ils se lisent dans l'URL courante et dans `document.referrer`, sans AUCUN accès
 *     au terminal (ni cookie posé, ni cookie lu) — ils ne relèvent donc pas de
 *     l'article 82 LIL / ePrivacy, exactement comme la propagation `?via=` affilié ;
 *   - ils ne sont JAMAIS transmis aux régies publicitaires : ils servent notre seule
 *     mesure interne (« d'où viennent nos inscrits ? »), y compris — et surtout —
 *     quand le visiteur a refusé les cookies publicitaires. Sans eux, un inscrit non
 *     consentant est un inscrit sans origine connue, définitivement.
 */

/**
 * Param d'URL par lequel la vitrine transmet à `app.propelsia.ai/register` le domaine
 * référent de la VISITE (ex. `chatgpt.com`). Indispensable : le referrer que voit la
 * page d'inscription est la vitrine elle-même, l'origine réelle est perdue au saut de
 * sous-domaine.
 */
export const REFERRER_DOMAIN_QUERY_PARAM = 'ref_domain';

/**
 * Param d'URL par lequel la vitrine transmet à `/register` le chemin de la PREMIÈRE page
 * vue de la visite (`/beaute`, `/bien-etre`…). Chemin seul, jamais la query : elle peut
 * porter des données personnelles et ses paramètres utiles voyagent déjà un par un.
 */
export const LANDING_PATH_QUERY_PARAM = 'lp_path';

/** Longueur max du chemin de landing page stocké. */
export const LANDING_PATH_MAX_LENGTH = 255;

/**
 * Variante d'A/B test de landing page vue par le visiteur (`lp_experiment` = id du
 * test, `lp_variant` = lettre, `lp_assignment` = `random` pour un tirage, `forced`
 * pour un lien `?v=` : seul `random` compte dans la comparaison, `lp_angle` = angle de
 * la pub d'arrivée qui a orienté le tirage, ou `aucun`), posée par la vitrine en
 * super-propriétés PostHog.
 *
 * Par défaut posthog-js garde les super-propriétés dans le `localStorage` du
 * sous-domaine courant, que `app.propelsia.ai` ne voit pas : seules celles listées
 * dans `cookie_persisted_properties` passent dans le cookie `.propelsia.ai`. Vitrine
 * ET app déclarent donc cette liste — l'app, pour que son propre `save()` ne les
 * efface pas du cookie. C'est ce qui découpe le funnel LP → inscription par variante.
 * Le cookie, écrit par la vitrine, fait foi : l'app s'y réaligne à chaque chargement
 * (cf. `getPostHogBootstrapConfig` du frontend-web).
 */
export const LP_EXPERIMENT_SUPER_PROPERTIES = ['lp_experiment', 'lp_variant', 'lp_assignment', 'lp_angle'] as const;

/**
 * Bras de l'A/B de la LP transporté dans l'URL (`?lp=b`) : posé par les CTA de la vitrine
 * vers la page d'inscription et par le middleware vers le quiz (`/start?lp=c`), puis stocké
 * sur l'inscrit (`marketing_attribution.lp_variant`) et sur le lead du quiz. Hors régime
 * cookies (simple paramètre d'URL) : transmis sans condition, comme les `utm_*`. Seul un
 * bras TIRÉ au sort voyage ainsi ; un forçage `?v=` n'est pas reporté.
 */
export const LP_VARIANT_FORWARD_QUERY_PARAM = 'lp';

/** Longueur max de `lp_variant` stocké (lettre du bras, ou valeur d'un lien de campagne). */
export const LP_VARIANT_MAX_LENGTH = 64;

/** Bras tel qu'on le stocke : minuscules, chiffres, `_` et `-`, borné ; sinon `undefined`. Forgeable, donc filtré. */
export function sanitizeLpVariant(value: string | null | undefined): string | undefined {
  const clean = value?.trim().toLowerCase() ?? '';
  return /^[a-z0-9_-]{1,64}$/.test(clean) ? clean : undefined;
}

// Le 2e caractère ne peut pas être `/` : `//evil.example/x` est une URL protocol-relative,
// donc une adresse EXTERNE que tout consommateur rendant la valeur en `href` suivrait
// (open redirect), et qui polluerait la mesure des pages d'entrée.
const LANDING_PATH_PATTERN = /^\/(?!\/)[A-Za-z0-9\-._~/%]*$/;

/**
 * Normalise et VALIDE un chemin de landing page venu de l'URL (forgeable) : chemin absolu
 * (jamais une URL, même protocol-relative) sans query ni fragment, caractères d'URL sûrs
 * uniquement. Sinon `undefined`.
 */
export function sanitizeLandingPath(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const path = value.trim().split(/[?#]/)[0];
  if (path.length === 0 || path.length > LANDING_PATH_MAX_LENGTH) return undefined;
  return LANDING_PATH_PATTERN.test(path) ? path : undefined;
}

/**
 * Identifiants publicitaires posés par Meta dans l'URL de destination (`hsa_cam`,
 * `hsa_grp`, `hsa_ad`, remplis à la diffusion). Contrairement à `utm_campaign`, figé au
 * nom de la campagne le jour de la publication, ils ne changent jamais : c'est la clé
 * fiable pour relier un inscrit à sa campagne et à sa pub. Simples paramètres d'URL,
 * même régime que les `utm_*` (mesure interne, jamais renvoyés aux régies).
 * Clé = param d'URL, valeur = champ stocké dans l'attribution du pro.
 */
export const META_AD_URL_PARAMS = {
  hsa_cam: 'meta_campaign_id',
  hsa_grp: 'meta_adset_id',
  hsa_ad: 'meta_ad_id',
} as const;

/** Identifiant numérique de régie (Meta : 15 à 20 chiffres). Tout le reste vaut `undefined`. */
export function sanitizeAdPlatformId(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const id = value.trim();
  return /^[0-9]{1,32}$/.test(id) ? id : undefined;
}

/** Longueur max stockée (bornes des schémas d'attribution). */
export const REFERRER_DOMAIN_MAX_LENGTH = 255;

/**
 * Hôte plausible : labels alphanumériques séparés par des points, au moins un point.
 * Volontairement strict — cette valeur finit en base et dans un salon d'équipe.
 */
const HOSTNAME_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

/**
 * Normalise et VALIDE un domaine référent venu de l'extérieur — typiquement le param
 * `?ref_domain=`, entièrement forgeable par le visiteur. Tout ce qui n'est pas un hôte
 * plausible vaut `undefined`.
 *
 * ⚠️ Point de passage obligé : sans lui, n'importe qui pouvait écrire une chaîne
 * arbitraire dans `professional.marketing_attribution` puis dans le champ « Origine »
 * du salon #acquisition, simplement en ouvrant `/register?ref_domain=…`.
 */
export function sanitizeReferrerDomain(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const host = value
    .trim()
    .toLowerCase()
    .replace(/^www\./, '');
  if (host.length === 0 || host.length > REFERRER_DOMAIN_MAX_LENGTH) return undefined;
  return HOSTNAME_PATTERN.test(host) ? host : undefined;
}

/**
 * Suffixes d'hôtes qui sont NOUS (vitrine, app, sites clients, environnements de dev) :
 * un referrer interne n'est pas une origine d'acquisition, c'est une navigation.
 */
const INTERNAL_HOST_SUFFIXES = [
  'propelsia.ai',
  'propelsia.com',
  'propelsia.site',
  'cfixe.com',
  'cfixe.site',
  'cfixe.app',
  'localhost',
  'nip.io',
];

function isInternalHost(host: string, currentHost?: string): boolean {
  if (currentHost && host === currentHost.toLowerCase().replace(/^www\./, '')) return true;
  return INTERNAL_HOST_SUFFIXES.some((suffix): boolean => host === suffix || host.endsWith(`.${suffix}`));
}

/**
 * Domaine du site référent quand il est EXTERNE (`chatgpt.com`, `google.com`…), sinon
 * `undefined`. On ne garde que l'hôte : le chemin d'une page référente peut porter des
 * données personnelles (recherche, identifiant de session) sans rien apporter à la
 * mesure du canal.
 */
export function extractExternalReferrerDomain(referrer: string | undefined, currentHost?: string): string | undefined {
  if (!referrer) return undefined;
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return undefined;
  }
  if (host.length === 0 || isInternalHost(host, currentHost)) return undefined;
  // Même filtre que pour `?ref_domain=` : une seule définition de « domaine acceptable ».
  return sanitizeReferrerDomain(host);
}
